function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import { REPORT_BASICS_LINKS, ReportName } from 'common/report/constants';
import { FilterSection } from 'common/report/insights/models/section';
import { getEntries } from 'common/report/insights/util/events';
import { getMetricEntriesFromShowClauses } from 'common/report/insights/util/metrics';
import { getPropertyFilters, getPropertyFiltersOperator } from 'common/report/insights/util/filters';
import { getSelectedSegments } from 'common/report/insights/util/segments';
import { getDateRange } from 'common/report/insights/util/time';
import { getMultiAttributionProperty } from 'common/report/insights/util';
import { SearchDisplayContext } from 'common/report/util/search/types';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { QueryBuilderProps, QueryBuilderUpdateDetail } from 'common/query-builder/query-builder/types';
import { QueryFilterEntry, QueryMultiMetricEntry, QuerySegmentEntry, QuerySuperEntry, RowLabelTheme, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { ReportQueryBuilder } from 'common/report/report-manager/query-builder';
import { InsightsQueryError, InsightsQueryResults } from 'common/report/insights/util/types';
import { ChartType, FUNNELS_MATH_TYPES, InsightsBookmarkParams, MetricType } from 'common/types/reports/bookmark';
import { LiftType } from 'common/report/util/lift/types';
import { getFirstSegment, isCohortSegment } from 'common/query-builder/query-block/segment-menu/types';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { canUseViewN } from 'common/components/data-table/utils';
import { isMultiAttributionSegmentation } from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import { addToast } from 'common/components/toast-area/toast-manager';
import { getFormulaDisabledReason, getFormulas } from 'common/query-builder/insights-formula/utils';
import { hasFunnelMetric, hasFunnelTimeToConvertBreakdownBookmark } from 'common/report/insights/models/utils';
import { RollingWindowSize } from 'common/util';
import { PropertySectionItem } from 'common/query-builder/query-block/properties-menu/item-menu-types';
import { Segmentation } from 'common/types/reports/query';
import { isFirstClassMetricsEnabled, isMetricLevelDataGroupsEnabled } from 'common/report/util/feature-sampling';
import { IncludeCustomPropertyType } from 'common/query-builder/query-block/properties-menu/types';
import { transformToGlobalQueryFilterEntries, transformToQuerySegmentEntries } from 'common/query-builder/query-builder/util';
import { INSIGHTS_DEFAULT_EVENT, INSIGHTS_DEFAULT_METRIC, Report } from '../models/report';
import { BehaviorShowClause } from '../models/show-clause';
import { BuilderSections } from '../models/builder-sections';
import { hasValidBookmarkParams } from '../contexts/validation';
import { updateReportFromQBSelection } from './qb-update';
import { getOtherAggregationOptions, getOtherFilterOptions, getOtherGroupByOptions } from './utils';
type InsightsQueryBuilderState = {
  report: Report | null;
};
class InsightsQueryBuilder extends ReportQueryBuilder<InsightsBookmarkParams, InsightsQueryResults, InsightsQueryBuilderState> {
  constructor() {
    super(...arguments);
    _defineProperty(this, "displayContext", SearchDisplayContext.Insights);
    _defineProperty(this, "reportType", ReportName.Insights);
    _defineProperty(this, "_error", null);
  }
  get defaultState() {
    return {
      report: null
    };
  }
  bookmarkUpdateFromQbUpdate(updateDetail: QueryBuilderUpdateDetail): InsightsBookmarkParams {
    var _this$params$project$;
    const oldReport = this.state.report;
    const hasMetricLevelDataGroups = isMetricLevelDataGroupsEnabled(this.params.project);
    const hasFirstClassMetrics = isFirstClassMetricsEnabled(this.params.project);
    const report = updateReportFromQBSelection({
      selection: updateDetail,
      report: this.state.report,
      customEvents: this.params.customEvents,
      canUseViewN: canUseViewN(),
      metricsResource: this.getContext("metrics"),
      behaviorsResource: this.getContext("behaviors"),
      projectTimezone: (_this$params$project$ = this.params.project.timezone) !== null && _this$params$project$ !== void 0 ? _this$params$project$ : null,
      projectAsOfDate: this.params.project.projectAsOfDate,
      metricLevelDataGroups: hasMetricLevelDataGroups,
      hasFirstClassMetrics
    });
    // add a toast with cta to change count type to total-conversions + compare to overall + bar chart if ttc breakdown is added
    if (hasFunnelTimeToConvertBreakdownBookmark(report.toBookmarkData()) && !hasFunnelTimeToConvertBreakdownBookmark(oldReport.toBookmarkData())) {
      const reportToConfirm = report.clone();
      reportToConfirm.sections.show.clauses.forEach(clause => {
        if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
          clause.measurement.math = FUNNELS_MATH_TYPES.GENERAL;
          clause.measurement.property = null;
        }
      });
      reportToConfirm.displayOptions.chartType = ChartType.Bar;
      // Time to convert breakdown is kinda ok
      reportToConfirm.liftComparison = {
        type: LiftType.Overall
      };
      // This update is for confirm all of ChartType + Measurement + LiftType changes
      addToast({
        message: "Show distribution of total conversions by time to convert.",
        cta: "Apply",
        timeout: 20000,
        ctaCallback: () => {
          this.dispatchUpdate({
            params: reportToConfirm.toBookmarkData(),
            queryReason: updateDetail.queryReason,
            requery: true
          });
        }
      });
    }
    return report.toBookmarkData();
  }
  public connectedCallback() {
    super.connectedCallback();
    this.update({
      report: Report.fromBookmarkData(this.params.bookmarkParams)
    });
  }
  qbChildrenTemplate() {
    return null;
  }
  reportSpecificQBParams(): Partial<QueryBuilderProps> {
    var _this$params$results, _this$params$dashboar, _this$params$dashboar2, _this$params$dashboar3, _this$params$dashboar4;
    const hasFirstTimeMetrics = isFirstClassMetricsEnabled(this.params.project);
    if (!this.state.report) {
      return {};
    }
    const {
      displayOptions,
      sections
    } = this.state.report;
    const bookmarkParams = this.state.report.toBookmarkData();
    const showSection = bookmarkParams.sections.show;

    /** while are are releasing a metrics-only QB, we will use one of these two functions to generate QB UI state */
    const entriesFn = hasFirstTimeMetrics ? getMetricEntriesFromShowClauses : getEntries;
    let entries = entriesFn({
      showClauses: showSection,
      behaviorsResource: this.getContext("behaviors"),
      dataGroups: this.params.dataGroups,
      queryError: this.params.error,
      dateUnit: sections.time.clauses[0].unit,
      reportSectionsMetadata: (_this$params$results = this.params.results) === null || _this$params$results === void 0 || (_this$params$results = _this$params$results.meta) === null || _this$params$results === void 0 ? void 0 : _this$params$results.report_sections
    });
    const dataGroupId = sections.getDataGroupId();
    const numBoardFilters = (_this$params$dashboar = (_this$params$dashboar2 = this.params.dashboardOverrides) === null || _this$params$dashboar2 === void 0 || (_this$params$dashboar2 = _this$params$dashboar2.additionalFilters) === null || _this$params$dashboar2 === void 0 ? void 0 : _this$params$dashboar2.length) !== null && _this$params$dashboar !== void 0 ? _this$params$dashboar : 0;
    const numBoardBreakdowns = (_this$params$dashboar3 = (_this$params$dashboar4 = this.params.dashboardOverrides) === null || _this$params$dashboar4 === void 0 || (_this$params$dashboar4 = _this$params$dashboar4.additionalBreakdowns) === null || _this$params$dashboar4 === void 0 ? void 0 : _this$params$dashboar4.length) !== null && _this$params$dashboar3 !== void 0 ? _this$params$dashboar3 : 0;
    const segments = getSelectedSegments(sections.group.clauses);
    const groupBy: QuerySegmentEntry[] = segments.map((segment, idx) => {
      const entry = transformToQuerySegmentEntries([segment])[0];
      return _objectSpread(_objectSpread({}, entry), {}, {
        // todo: why is qb deciding which segments are draggable or not??
        undraggable: isCohortSegment(segment) || isMultiAttributionSegmentation(getFirstSegment(segment)),
        inheritedFromBoard: idx >= segments.length - numBoardBreakdowns
      });
    });
    const propertyFilters = getPropertyFilters(sections.filter.clauses);
    const filters: QueryFilterEntry[] = transformToGlobalQueryFilterEntries(propertyFilters).map((entry, idx) => {
      return _objectSpread(_objectSpread({}, entry), {}, {
        inheritedFromBoard: idx >= propertyFilters.length - numBoardFilters
      });
    });
    const reportName = this.params.insightsReportMode === BookmarkType.Funnels ? ReportName.Funnels : this.params.insightsReportMode === BookmarkType.Retention ? ReportName.Retention : ReportName.Insights;
    if (reportName === ReportName.Insights && showSection.length === 1 && !hasValidBookmarkParams(bookmarkParams)) {
      entries = [];
    }
    const multiAttributionProperty = getMultiAttributionProperty(sections.group.clauses);
    const hasMultiAttributionProp = Boolean(multiAttributionProperty);
    const formulasDisableReason = getFormulaDisabledReason(this.state.report);
    return _objectSpread(_objectSpread({
      allowCohortFilter: true,
      disableAddEntry: [BookmarkType.Funnels, BookmarkType.Retention].includes(this.params.insightsReportMode),
      addEntryButtonLabel: this.t("query-builder.add-metric"),
      addEntryButtonTooltipText: this.t("query-builder.add-metric-tooltip", {
        url: REPORT_BASICS_LINKS[ReportName.Insights],
        interpolation: {
          escapeValue: false
        }
      }),
      canCustomizeNumericBuckets: true,
      canUseMultiAttribution: hasMultiAttributionProp,
      chartType: this.state.report.displayOptions.chartType
    }, dataGroupId ? {
      dataGroupId
    } : {}), {}, {
      dateRange: getDateRange(sections.time.clauses).getAttributes(),
      dateUnit: sections.time.clauses[0].unit as unknown as RollingWindowSize,
      disableAddEntryReason: this.state.report.displayOptions.chartType === ChartType.Table && this.state.report.hasTopEvents() ? "Events cannot be added on tables with \"Your Top Events\"." : "",
      disableBreakdown: displayOptions.chartType === ChartType.FunnelTopPaths,
      enableEnhancedListFilters: true,
      enableHideEntry: true,
      entryRowLabelType: RowLabelType.Alpha,
      entryRowLabelTheme: RowLabelTheme.Gray,
      entriesDraggable: this.params.insightsReportMode === BookmarkType.Insights,
      entries,
      minEntries: 1,
      emptyPlaceholder: reportName === ReportName.Insights ? INSIGHTS_DEFAULT_METRIC as QueryMultiMetricEntry : _objectSpread(_objectSpread({}, INSIGHTS_DEFAULT_EVENT), {}, {
        dataGroupId
      }) as QuerySuperEntry,
      includeCustomProperties: IncludeCustomPropertyType.All,
      showBorrowedProperties: true,
      filtersOperator: getPropertyFiltersOperator(sections.filter.determiner || FilterSection.DETERMINER_ALL),
      filters,
      formulas: getFormulas(sections.formula.clauses),
      formulasDisableReason,
      formulasError: this.params.error,
      groupBy,
      hideDatePicker: true,
      metricsMode: true,
      otherFilterOptions: this.getOtherFilterOptions(multiAttributionProperty),
      otherGroupByOptions: this.getOtherGroupByOptions(sections),
      otherAggregationOptions: this.getOtherAggregationOptions(),
      plotStyle: displayOptions.plotStyle,
      queryTypeOptions: [{
        value: MetricType.Retention,
        label: _ref => {
          let {
            t
          } = _ref;
          return t("retention.retention");
        },
        tooltipLabel: _ref2 => {
          let {
            t
          } = _ref2;
          return "".concat(t("retention.retention-tooltip"), "\n[").concat(t("common.learn-more"), "](https://docs.mixpanel.com/docs/analysis/reports/retention#overview)");
        }
      }, {
        value: MetricType.RetentionFrequency,
        label: _ref3 => {
          let {
            t
          } = _ref3;
          return t("retention.frequency");
        },
        tooltipLabel: _ref4 => {
          let {
            t
          } = _ref4;
          return "".concat(t("retention.frequency-tooltip"), "\n[").concat(t("common.learn-more"), "](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)");
        }
      }],
      selectedQueryType: this.params.insightsReportMode === BookmarkType.Retention ? (sections.show.clauses[0] as BehaviorShowClause).behavior.type : null,
      rowTypes: [SelectorTypeDefinitions.Metrics],
      showEntryRename: true,
      showSpecialDatasetProperty: this.params.project.isRollupProject(),
      /**
       * Hide Top Events from events-menu if table chart has another metric already. Insights table UI for
       * column/metric labeling looks weird when Top Events is mixed with other events
       */
      restrictTopEventsOnly: this.state.report.displayOptions.chartType === ChartType.Table,
      supportB2bCohorts: this.canUseDataGroups(),
      switchableReportName: reportName,
      reportName,
      trackName: reportName,
      // disable profile history in profiles mode for now. need alignment on whether it's useful
      disableProfileHistory: this.state.report.sections.show.isPeopleOnlyQuery(),
      enableFilterAttribution: hasFunnelMetric(bookmarkParams),
      breakdownAttribution: []
    });
  }
  onBookmarkUpdated(newBookmarkParams: InsightsBookmarkParams) {
    this.update({
      report: Report.fromBookmarkData(newBookmarkParams)
    });
  }
  private getOtherAggregationOptions(): Array<PropertySectionItem<string>> {
    return getOtherAggregationOptions({
      hasEventsQuery: this.state.report.sections.show.hasEventsQuery(),
      insightsReportMode: this.params.insightsReportMode
    });
  }
  private getOtherFilterOptions(multiAttributionProperty: Segmentation | null): Array<PropertySectionItem<string>> {
    return getOtherFilterOptions({
      showClauses: this.state.report.sections.show.clauses,
      hasGlobalDataGroup: this.hasGlobalDataGroup(),
      insightsReportMode: this.params.insightsReportMode,
      multiAttributionProperty,
      currentProject: this.params.project
    });
  }
  private getOtherGroupByOptions(sections: BuilderSections): Array<PropertySectionItem<string>> {
    return getOtherGroupByOptions({
      showClauses: sections.show.clauses,
      hasGlobalDataGroup: this.hasGlobalDataGroup(),
      insightsReportMode: this.params.insightsReportMode,
      currentProject: this.params.project,
      metricsResource: this.getContext("metrics"),
      behaviorsResource: this.getContext("behaviors")
    });
  }
  private hasGlobalDataGroup() {
    return Boolean(this.params.bookmarkParams.sections.globalDataGroupId);
  }
  canUseBehaviors() {
    return !this.state.report.sections.show.hasPeopleQuery();
  }
  canUsePageDuration() {
    return this.params.insightsReportMode === BookmarkType.Insights;
  }
}
export const InsightsQueryBuilderEl = defineAndMakeJsxFactory("insights-query-builder", InsightsQueryBuilder);
export const InsightsQueryBuilderReact = panel2react("insights-query-builder", InsightsQueryBuilder);